import { KbaQuestionType } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../contexts/data-context/use-data';
import { CardView } from '../../../types/entity-types';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';

const CARDS_API_PATH = '/api/cards/virtual';

/**
 * KBA entry for 3D Secure authentication
 */
export type KbaEntry = {
  type: KbaQuestionType;
  answer: string;
};

/**
 * Request type for creating a virtual card
 */
export type CreateVirtualCardRequest = {
  accountId: string;
  displayName?: string;
  knowledgeBase: KbaEntry[];
};

type UseCreateCardOptions = {
  onViewCard?: (card: CardView) => void;
};

export const useCreateCard = (options?: UseCreateCardOptions) => {
  const [isCreating, setIsCreating] = useState(false);
  const { data, mutations } = useData();

  const { trigger } = useSWRMutation<CardView, Error, string, CreateVirtualCardRequest>(CARDS_API_PATH, postSwrFetcher);

  const createCard = async (request: CreateVirtualCardRequest): Promise<CardView | undefined> => {
    try {
      setIsCreating(true);

      const newCard = await trigger(request);

      if (newCard) {
        toast.success('Card created', {
          action: {
            label: 'View card',
            onClick: () => {
              options?.onViewCard?.(newCard);
            },
          },
        });

        if (data?.cards) {
          await mutations.mutateCache?.({
            cards: [...data.cards, newCard],
          });
        }
      }

      return newCard;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          request: {
            accountId: request.accountId,
            displayName: request.displayName,
            knowledgeBaseCount: request.knowledgeBase.length,
          },
          context: 'createCard',
        },
      });
      toast.error('Failed to create card. Please try again.');
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createCard,
    isCreating,
  };
};
